import "./styles.css";
import { BattleController } from "./game/battle/BattleController";
import { BattleInteractionRouter, type BattleTeam } from "./game/battle/BattleInteractionRouter";
import { BattleWorld } from "./game/battle/BattleWorld";
import { BattleMockSource } from "./live/BattleMockSource";
import { LiveEventHub } from "./live/protocol";
import { GatewayLiveSource } from "./live/websocket";

const element = <T extends HTMLElement>(id: string): T => {
  const value = document.getElementById(id);
  if (!value) throw new Error(`Missing #${id}`);
  return value as T;
};

const startButton = element<HTMLButtonElement>("start-button");
startButton.disabled = true;
startButton.textContent = "正在铺好梦毯…";

const params = new URLSearchParams(location.search);
const liveMode = params.get("mode") === "live";

const world = await BattleWorld.create(element("viewport"));
const hub = new LiveEventHub();
const router = new BattleInteractionRouter();
const mock = new BattleMockSource(hub);
if (liveMode) mock.setAuto(false);
const game = new BattleController(world, hub, router, mock, {
  simulationEnabled: !liveMode,
  exposeDebugAPI: !liveMode,
});

try {
  await world.loadCommanders();
} catch (error) {
  console.warn("Pillow Fight commanders could not be loaded; using toon stand-ins.", error);
}

startButton.disabled = false;
startButton.textContent = "开始双队直播互动";
element("gateway-status").textContent = `开发模拟 · ${world.backend.toUpperCase()}`;

if (liveMode) {
  element("simulator").hidden = true;
  element("simulator-toggle").hidden = true;
}

const gatewayUrl = params.get("gateway");
if (gatewayUrl) {
  const gateway = new GatewayLiveSource(hub, (status) => {
    const label = status === "connected" ? "直播网关已连接" : status === "connecting" ? "正在连接网关" : "网关离线 · 模拟可用";
    element("gateway-status").textContent = label;
    element("gateway-status").dataset.status = status;
  });
  gateway.connect(gatewayUrl);
}

let selectedTeam: BattleTeam = "ember";
const setSelectedTeam = (team: BattleTeam) => {
  selectedTeam = team;
  element("sim-team-ember").classList.toggle("is-selected", team === "ember");
  element("sim-team-aqua").classList.toggle("is-selected", team === "aqua");
  element("selected-team-label").textContent = team === "ember" ? "火绒小队" : "月潮小队";
};
element("sim-team-ember").addEventListener("click", () => setSelectedTeam("ember"));
element("sim-team-aqua").addEventListener("click", () => setSelectedTeam("aqua"));

const preview = element("protocol-preview");
const autoToggle = element<HTMLButtonElement>("auto-toggle");
autoToggle.addEventListener("click", () => {
  mock.setAuto(!mock.auto);
  autoToggle.classList.toggle("is-on", mock.auto);
  autoToggle.setAttribute("aria-pressed", String(mock.auto));
});

element<HTMLFormElement>("comment-form").addEventListener("submit", (event) => {
  event.preventDefault();
  const text = element<HTMLInputElement>("comment-input").value.trim().slice(0, 10);
  if (text.includes("红")) mock.emitJoin("ember");
  else if (text.includes("蓝")) mock.emitJoin("aqua");
  else mock.emitCommand(selectedTeam, text.includes("守") || text.includes("盾") ? "守" : "冲");
  preview.textContent = `live_comment · content: ${text || "冲"} · ${selectedTeam}`;
});
document.querySelectorAll<HTMLButtonElement>("[data-command]").forEach((button) => {
  button.addEventListener("click", () => {
    const command = button.dataset.command ?? "冲";
    if (command === "红队") mock.emitJoin("ember");
    else if (command === "蓝队") mock.emitJoin("aqua");
    else mock.emitCommand(selectedTeam, command === "守" ? "守" : "冲");
    preview.textContent = `live_comment · content: ${command}`;
  });
});

element("send-likes").addEventListener("click", () => {
  mock.emitLikes(selectedTeam, 20);
  preview.textContent = `live_like · like_num: 20 · ${selectedTeam}`;
});
element("send-follow").addEventListener("click", () => {
  mock.emitFollow(selectedTeam);
  preview.textContent = `live_fansclub · reason_type: 2 · ${selectedTeam}`;
});
element("send-reinforce").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "reinforce");
  preview.textContent = `live_gift · gift_value: 100 · ${selectedTeam}`;
});
element("send-artillery").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "artillery");
  preview.textContent = `live_gift · gift_value: 300 · ${selectedTeam}`;
});
element("send-flood").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "flood");
  preview.textContent = `live_gift · sec_gift_id: pajama-flood · gift_value: 500 · ${selectedTeam}`;
});
element("send-catrush").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "catrush");
  preview.textContent = `live_gift · sec_gift_id: pajama-mango-cat-rush · gift_value: 650 · ${selectedTeam}`;
});
element("send-mech").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "mech");
  preview.textContent = `live_gift · sec_gift_id: pajama-biped-mech · gift_value: 750 · ${selectedTeam}`;
});
element("send-rift").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "rift");
  preview.textContent = `live_gift · sec_gift_id: pajama-earth-rift · gift_value: 800 · ${selectedTeam}`;
});
element("send-hound").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "hound");
  preview.textContent = `live_gift · sec_gift_id: pajama-quadruped-robo-hound · gift_value: 900 · ${selectedTeam}`;
});
element("send-tornado").addEventListener("click", () => {
  mock.emitGift(selectedTeam, "tornado");
  preview.textContent = `live_gift · gift_value: 1000 · ${selectedTeam}`;
});
element("send-crowd").addEventListener("click", () => {
  game.seedBattle(1_000);
  preview.textContent = "battle_load · 1,000 viewers · balanced teams";
});
element("send-chaos").addEventListener("click", () => {
  if (game.snapshot().phase !== "playing") game.start();
  mock.replayChaos(240);
  preview.textContent = "replay · reorder + duplicate + opposing gifts";
});

window.setInterval(() => {
  const snapshot = game.snapshot();
  element("runtime-backend").textContent = snapshot.graphics.gpuSimulation
    ? `${snapshot.renderer.toUpperCase()} GPU-SIM`
    : `${snapshot.renderer.toUpperCase()} CPU-FB`;
  element("runtime-fps").textContent = String(Math.round(snapshot.graphics.fps));
  element("runtime-calls").textContent = `${snapshot.graphics.drawCalls} / ${snapshot.graphics.computeCalls}`;
  element("runtime-players").textContent = `${snapshot.crowd.logicalParticipants.toLocaleString("zh-CN")} / ${snapshot.crowd.visibleParticipants.toLocaleString("zh-CN")}`;
  element("runtime-vfx").textContent = `${snapshot.graphics.gpuVfx.activeParticles.toLocaleString("zh-CN")} / ${snapshot.graphics.gpuVfx.capacity.toLocaleString("zh-CN")}`;
}, 450);

const simulator = element("simulator");
element("simulator-toggle").addEventListener("click", () => simulator.classList.toggle("is-open"));
element("game-stage").addEventListener("pointerdown", (event) => {
  if (event.target === element("viewport") && innerWidth < 980) simulator.classList.remove("is-open");
});

setSelectedTeam("ember");
window.addEventListener("beforeunload", () => game.dispose());
